import { Pojo } from "types/Galaxy";
import { format, formatISO, isValid, parseISO } from "date-fns";

export type CalendarEditorVariant = "date" | "dateTime" | "dateTimeSeconds";
export const editorFormats: Record<CalendarEditorVariant, string> = {
  date: "yyyy-MM-dd",
  dateTime: "yyyy-MM-dd'T'HH:mm",
  dateTimeSeconds: "yyyy-MM-dd'T'HH:mm:ss",
};
export const editorFormatBySjcoType: Record<string, string> = {
  CA: editorFormats.date,
  CAH: editorFormats.dateTime,
  CAHS: editorFormats.dateTimeSeconds,
};

/** Parse une valeur date (ISO, yyyy-MM-dd, Date) en Date locale, comme Calendar.tsx. */
export function toLocalDate(value: unknown): Date | null {
  if (value == null || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }
  if (typeof value === "number") {
    const date = new Date(value);
    return isValid(date) ? date : null;
  }
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);
      // new Date impose que l'année soit >= 1900
      if (year > 1900) {
        const date = new Date(year, month - 1, day);
        return isValid(date) ? date : null;
      }
    }
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
  }
  return null;
}

export type CalendarColumnOptions = {
  /** Comme convertValue(e) du datatable classique : formatISO avec offset fuseau local. */
  outputIso?: boolean;
  /** Type AG Grid pour le formatage/parse des valeurs date (évite date seule à 00:00 pour les datetime). */
  cellDataType?: "dateString" | "dateTimeString";
};

export function getCalendarColumnProps(
  editorFormat: "yyyy-MM-dd" | "yyyy-MM-dd'T'HH:mm" | "yyyy-MM-dd'T'HH:mm:ss",
  options?: CalendarColumnOptions,
) {
  const formatForEditor = (value: unknown): string | null => {
    let date = toLocalDate(value);
    if (editorFormat === "yyyy-MM-dd") {
      const finalValue = date ? toNearestMidnight(date) : null;
      date = finalValue;
    }
    return date ? format(date, editorFormat) : null;
  };

  const formatForStorage = (value: unknown): string | null => {
    if (value == null || value === "") {
      return null;
    }
    const date = toLocalDate(value);
    if (!date) {
      return null;
    }
    if (options?.outputIso) {
      return formatISO(date);
    }
    return format(date, editorFormat);
  };

  return {
    ...(options?.cellDataType ? { cellDataType: options.cellDataType } : {}),
    valueGetter: (params: { data?: Pojo; colDef?: { field?: string } }) => {
      const field = params.colDef?.field;
      if (!field || !params.data) {
        return null;
      }
      return formatForEditor(params.data[field]);
    },
    valueParser: (params: { newValue: unknown }) => formatForStorage(params.newValue),
    valueFormatter: (params: { value: unknown }) => {
      const date = toLocalDate(params.value);
      if (!date) {
        return "";
      }
      if (editorFormat === "yyyy-MM-dd") {
        const finalValue = toNearestMidnight(date);
        return finalValue.toLocaleDateString();
      }
      const timeOptions: Intl.DateTimeFormatOptions =
        editorFormat === "yyyy-MM-dd'T'HH:mm"
          ? { hour: "2-digit", minute: "2-digit" }
          : { hour: "2-digit", minute: "2-digit", second: "2-digit" };
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString(undefined, timeOptions)}`;
    },
  };
}

/** Si l'heure n'est pas 00:00:00, ramène la date au minuit local le plus proche. */
export function toNearestMidnight(date: Date): Date {
  const hasTime =
    date.getHours() !== 0 ||
    date.getMinutes() !== 0 ||
    date.getSeconds() !== 0 ||
    date.getMilliseconds() !== 0;
  if (!hasTime) {
    return date;
  }

  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const nextDay = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  const distanceToDayStart = date.getTime() - dayStart.getTime();
  const distanceToNextDay = nextDay.getTime() - date.getTime();

  return distanceToNextDay < distanceToDayStart ? nextDay : dayStart;
}
